import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { Toast } from '../proposition-c/ui';
import { CreateWizard } from './CreateWizard';
import { HomePage } from './HomePage';
import { useProjects, useReferential } from './hooks';
import { ProjectView } from './ProjectView';
import { STUDIO_HEADER, UMR } from './tokens';

export const meta = {
  title: "Création d'un projet — v2",
  description:
    "Nouvelle version de « Création d'un projet » : même parcours (accueil, assistant 3 étapes, vue projet, paramètres, synchronisation UrbanMap), base de travail pour les évolutions en cours.",
  tags: ['wizard', 'planning', 'map'],
  author: 'plan-team',
  status: 'draft' as const,
  createdAt: '2026-10-20',
  updatedAt: '2026-10-29',
};

/** `?projets=0` dans l'URL affiche l'état vide de l'accueil. */
const emptyHome = () => new URLSearchParams(window.location.search).get('projets') === '0';

export default function PlanProjetChaleurV2() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('projet');
  const [empty] = useState(emptyHome);
  const projects = useProjects(empty);
  const referential = useReferential();
  const [wizard, setWizard] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const selectProject = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('projet', id);
    else next.delete('projet');
    setSearchParams(next);
  };

  const goHome = () => {
    setWizard(false);
    selectProject(null);
    void qc.invalidateQueries({ queryKey: ['plan2cost', 'projet-chaleur-v2', 'projects'] });
  };

  let body: JSX.Element;
  if (projectId) {
    body = referential.data
      ? <ProjectView key={projectId} projectId={projectId} referential={referential.data} onBack={goHome} flash={flash} />
      : <div style={{ padding: 24 }}>{referential.isError ? 'Erreur de chargement du référentiel.' : 'Chargement…'}</div>;
  } else {
    body = (
      <div style={{ position: 'relative', height: '100%' }}>
        <HomePage
          projects={projects.data}
          loading={projects.isLoading}
          error={projects.isError}
          onCreate={() => setWizard(true)}
          onOpen={(id) => { setWizard(false); selectProject(id); }}
          flash={flash}
        />
        {wizard && referential.data && (
          <CreateWizard
            referential={referential.data}
            onCancel={() => setWizard(false)}
            onOpen={(id) => {
              setWizard(false);
              selectProject(id);
              flash('Projet créé · copie des bâtiments UrbanMap figée à aujourd’hui');
            }}
          />
        )}
      </div>
    );
  }

  return (
    <PrototypeStudioLayout title={meta.title} commentScope={projectId ? `project-${projectId}` : wizard ? 'creation' : undefined}>
      <div data-comment-view={projectId ? `project-${projectId}` : wizard ? 'creation' : 'home'} style={{ position: 'relative', height: `calc(100vh - ${STUDIO_HEADER}px)`, overflow: 'hidden', fontFamily: UMR.font, background: UMR.white, color: UMR.textStrong }}>
        {body}
        {toast && <Toast>{toast}</Toast>}
      </div>
    </PrototypeStudioLayout>
  );
}
